import React, { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Button, { ILink } from 'tbk-components/src/components/Button';
import { IImage } from 'tbk-components/src/components/Image';
import Nav, { MMNavItem } from '../MegaMenus/Nav';
import AdvancedMedia from '../AdvancedMedia';
import Icon from 'tbk-components/src/components/Icon';
import TabsWithThumbnail from './TabsWithThumbnail/TabsWithThumbnail';
import TabWithThumbnail from './TabsWithThumbnail/TabWithThumbnail';
import MenuRenderer from './MenuRenderer';

export interface IMegaMenuTabsProps extends IBaseElementProps {
	menuSection?: IMenuSection;
	/** This will create a tab section used for the Product menu. Do not fill this out for Solutions. */
	menuTabs?: IMenuTab[];
	navsIcons?: boolean;
	enableSearch?: boolean;
	searchLabel?: string;
	/**
	 * @noUI
	 */
	searchAction?: string;
}

export interface IMegaMenuTabsCTAsProps {
	heading?: string;
	image?: IImage;
	description?: string;
	link?: ILink;
}

export interface IMenuSection extends IBaseElementProps {
	/**
	 * Select a WordPress menu to supply the nav items for this Mega Menu.
	 * @control NavItem[]
	 */
	menu?: MMNavItem[];
	ctaLink?: ILink;
	ctaTitle?: string;
	rightPanelTitle?: string;
	rightPanelTiles?: IMegaMenuTabsCTAsProps[];
	rightPanelListLinks?: IListLink[];
	disableGreyBackground?: boolean;
	navIcons?: boolean;
}
export interface IListLink extends IBaseElementProps {
	link?: ILink;
	/**
	 * @control iconselect
	 */
	icon?: string;
}
export interface IMenuTab extends IBaseElementProps {
	title?: string;
	thumbnail?: IImage;
	/**
	 * Setting this link will override tab functionality and simply link to the provided URL.
	 */
	tabLink?: ILink;
	/**
	 * Select a WordPress menu to supply the nav items for this Mega Menu.
	 * @control NavItem[]
	 */
	navs?: MMNavItem[];
	navIcons?: boolean;
	ctaLink?: ILink;
	/**
	 * Adding this title will add a background color to the CTA section as well.
	 */
	ctaTitle?: string;
	/**
	 * This is a manual number that will determine the order the CTA should show in the nav listing. By default the Nav will show at the end.
	 */
	ctaNavPosition?: number;
}

/**
 * Calian Mega Menus With Tabs
 * @icon format-gallery
 * @block
 */
const MegaMenuTabs: React.FC<IMegaMenuTabsProps> = ({
	className,
	classNames = [],
	menuSection,
	menuTabs,
	enableSearch = true,
	searchLabel = 'Search for a Calian product...',
	searchAction = '/',
}) => {
	const renderRightPanel =
		menuSection?.rightPanelTitle ||
		menuSection?.rightPanelTiles?.length > 0 ||
		menuSection?.rightPanelListLinks?.length > 0;
	const mmRef = useRef<HTMLDivElement>(null);

	const [advancedUrlWithQuery, setAdvancedUrlWithQuery] = useState<string>(
		searchAction || '',
	);

	// Dynamically set the max height of the mega menu
	useEffect(() => {
		const calcMegaMenuHeight = () => {
			if (!mmRef.current) {
				return;
			}

			if (window.innerWidth >= 1200) {
				const header = document.querySelector(
					'.calian-header',
				) as HTMLElement;
				mmRef.current.style.maxHeight = `${Math.floor(
					window.innerHeight - header?.offsetHeight,
				)}px`;
			} else {
				mmRef.current.style.maxHeight = '';
			}
		};

		calcMegaMenuHeight();
		window.addEventListener('resize', calcMegaMenuHeight);

		return () => {
			window.removeEventListener('resize', calcMegaMenuHeight);
		};
	}, []);

	return (
		<div
			className={cn(
				className || [
					'calian-mega-menus calian-mega-menus--at',
					...classNames,
				],
			)}
			ref={mmRef}
		>
			<div className="calian-mega-menus__container bg-white position-relative w-100 mx-auto">
				<div
					className={cn(
						'calian-mega-menus__wrapper w-100 d-flex m-0 flex-column',
					)}
				>
					{enableSearch && (
						<div className="calian-mega-menus__search-bar-wrapper w-100 px-3 py-4">
							<div className="calian-mega-menus__search-bar mw-1240px mx-auto">
								<form
									className="calian-header__mobile-search-form d-flex"
									action={advancedUrlWithQuery}
									method="get"
								>
									<input
										className="form-control"
										type="text"
										placeholder={searchLabel}
										aria-label="Search"
										onChange={(e) => {
											setAdvancedUrlWithQuery(
												`${searchAction}#!search=${e.target.value}`,
											);
										}}
									/>
									<button className="btn" type="submit">
										<i className="icon icon-magnifying-glass" />
										<span className="visually-hidden">
											Search
										</span>
									</button>
								</form>
							</div>
						</div>
					)}

					{menuSection?.menu?.length > 0 && (
						<div className="calian-mega-menus__menu-section position-relative z-1 px-3 py-6">
							<div className="menu-section__wrapper d-xl-flex mw-1240px mx-auto px-0">
								<MenuRenderer
									navs={menuSection.menu}
									classes={
										!renderRightPanel
											? ['no-right-panel']
											: []
									}
									navIcons={menuSection.navIcons}
									ctaLink={menuSection.ctaLink}
									ctaTitle={menuSection.ctaTitle}
								/>

								{renderRightPanel && (
									<div
										className={cn(
											'menu-section__cta-listing w-100 ps-lg-5',
											menuSection?.rightPanelTiles
												?.length > 3 &&
												'menu-section__cta-listing--two-col',
										)}
									>
										<div className="menu-section__content ps-xl-5 z-1 position-relative">
											{menuSection?.rightPanelTitle && (
												<h3 className="h6">
													{
														menuSection.rightPanelTitle
													}
												</h3>
											)}
											{menuSection?.rightPanelTiles &&
												menuSection.rightPanelTiles
													.length > 0 && (
													<div
														className={cn(
															'cta-listing-tiles z-1',
														)}
													>
														{menuSection.rightPanelTiles.map(
															(tile, key) => {
																return React.createElement(
																	tile.link
																		?.href
																		? 'a'
																		: 'div',
																	{
																		key: key,
																		className:
																			cn(
																				'cta-listing-tile d-flex align-items-center gap-4',
																			),
																		children:
																			(
																				<>
																					{tile.image && (
																						<AdvancedMedia
																							size="4:3"
																							classNames={[
																								'flex-shrink-0 flex-grow-1',
																								menuSection
																									?.rightPanelTiles
																									.length >
																								3
																									? 'rounded-3'
																									: 'rounded-5',
																							]}
																							image={
																								tile.image
																							}
																						/>
																					)}
																					<div className="cta-listing-tile__content flex-shrink-1">
																						{tile.heading && (
																							<h4 className="mb-0">
																								{
																									tile.heading
																								}
																							</h4>
																						)}
																						{tile.description && (
																							<div className="cta-listing-copy">
																								<p
																									className="mb-0"
																									dangerouslySetInnerHTML={{
																										__html: tile.description,
																									}}
																								/>
																							</div>
																						)}
																					</div>
																				</>
																			),
																		href: tile
																			.link
																			?.href,
																		target: tile
																			.link
																			?.target,
																	},
																);
															},
														)}
													</div>
												)}

											{menuSection.rightPanelListLinks &&
												menuSection.rightPanelListLinks
													.length > 0 && (
													<ul className="d-flex flex-column gap-4 list-unstyled calian-mega-menus__navs--icons calian-mega-menus__navs">
														{menuSection.rightPanelListLinks.map(
															(link, key) => {
																return (
																	<li
																		key={
																			key
																		}
																		className="nav-item mb-0"
																	>
																		<div className="nav-item-icon">
																			<Icon
																				name={
																					link?.icon
																				}
																			/>
																		</div>
																		<a
																			className="nav-link mb-0"
																			href={
																				link
																					?.link
																					?.href ||
																				'#'
																			}
																		>
																			{
																				link
																					?.link
																					?.label
																			}
																		</a>
																	</li>
																);
															},
														)}
													</ul>
												)}
										</div>
										{!menuSection.disableGreyBackground && (
											<div className="cta-listing-overlay position-absolute z-0 d-none d-xl-block h-100 w-100 top-0 bg-light"></div>
										)}
									</div>
								)}
							</div>
						</div>
					)}

					{menuTabs && menuTabs.length > 0 && (
						<div className="calian-mega-menus__menu-tabs mw-1288px mx-auto w-100">
							<TabsWithThumbnail>
								{menuTabs.map((tab, key) => {
									return (
										<TabWithThumbnail
											key={key}
											thumbnail={tab.thumbnail}
											title={tab.title}
											tabLink={tab.tabLink}
										>
											{tab.navs && (
												<MenuRenderer
													navs={tab.navs}
													navIcons={true}
													ctaLink={tab.ctaLink}
													ctaTitle={tab.ctaTitle}
													ctaNavPosition={
														tab.ctaNavPosition
													}
												/>
											)}
										</TabWithThumbnail>
									);
								})}
							</TabsWithThumbnail>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};
export default MegaMenuTabs;
